import { Immutable } from "immer";
import React from "react";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

type LooseStreamFieldBlock = {
    id: string;
    type: string;
    value: unknown;
};

type LooseBlockComponent = React.ComponentType<{
    id?: string;
    type?: string;
    index?: number;
    value: unknown;
}>;

type IProps = Immutable<{
    value: ReadonlyArray<LooseStreamFieldBlock>;
    components: Record<string, LooseBlockComponent>;
    bgColor?: string | null;
    /** Render blocks without the Wagtail `w-block-{type} block-{type}`
     * wrapper div. The wrapper mirrors the `{{ self.body }}` page assemblies
     * (Basic/Generic); maps mirroring a bare `{% include_block block %}` loop
     * (the PDP module assemblies) must skip it, or block-scoped CSS like
     * sealy's `.block-features_block` applies twice (wrapper + component
     * root). */
    bare?: boolean;
}>;

export const StyleguideBlockRenderer = (props: IProps): React.JSX.Element => {
    const content = props.value.map((block) => {
        // A missing map entry renders a bare `undefined` component, which
        // fails deep inside StreamField with an opaque React error (worse
        // inside SSR). Fail loudly at the mapping boundary instead.
        if (!(block.type in props.components)) {
            throw new Error(
                `No component registered for block type "${block.type}" ` +
                    `(map keys: ${Object.keys(props.components).join(", ")})`,
            );
        }
        if (props.bare) {
            return (
                <StreamField<ReadonlyArray<LooseStreamFieldBlock>>
                    key={block.id}
                    tagName={React.Fragment}
                    value={[block]}
                    components={props.components}
                />
            );
        }
        return (
            <StreamField<ReadonlyArray<LooseStreamFieldBlock>>
                key={block.id}
                tagName="div"
                attrs={{
                    className: `w-block-${block.type} block-${block.type}`,
                }}
                value={[block]}
                components={props.components}
            />
        );
    });
    if (props.bgColor) {
        return <div style={{ backgroundColor: props.bgColor }}>{content}</div>;
    }
    return <>{content}</>;
};
